import { HelpLink } from "../help/links";
import { formatDate, formatMoney, formatPercentAnnual, formatRateHundredths } from "../lib/format";
import type { AccrualView, SanctionCalc } from "../lib/legal";
import { LegalDisclaimer } from "./LegalDisclaimer";

/**
 * Карточка расчёта санкций (E9, docs/07 — компонент `CalcBreakdown`): таблица периодов
 * «как посчитано» + итог + правовая оговорка ТЗ 4.2.5.
 *
 * Ни одного рубля здесь не считается — всё приходит с сервера (ядро E4). Задача компонента —
 * показать расчёт так, чтобы юрист мог перенести его в претензию и сверить вручную:
 *   • по каждому периоду видно база × ставка × дни = сумма;
 *   • видно, ПОЧЕМУ период разбит (частичная оплата/отгрузка меняет базу, смена ключевой ставки);
 *   • при потолке объяснено, почему сумма периодов больше итога;
 *   • при переопределении даты начисления видны ОБЕ даты и основание.
 */

const cellCls = "px-2 py-1.5 align-top";
const headCls = "px-2 py-1.5 text-left text-[12px] font-bold text-muted";

/** Заголовок и подпись одного расчёта: неустойка или проценты по ст. 395. */
function accrualTitle(kind: "penalty" | "interest395", primary: SanctionCalc["primary"]): string {
  const name = kind === "penalty" ? "Договорная неустойка" : "Проценты по ст. 395 ГК РФ";
  return primary === kind ? `${name} — основной расчёт` : `${name} — справочно`;
}

function AccrualTable({
  kind,
  accrual,
  primary,
}: {
  kind: "penalty" | "interest395";
  accrual: AccrualView;
  primary: SanctionCalc["primary"];
}) {
  // Сумма периодов нужна только при потолке — объяснить расхождение с итогом.
  const periodsSum = accrual.periods.reduce((acc, p) => addMoney(acc, p.sum), "0.00");

  return (
    <section className="mt-4" aria-labelledby={`calc-${kind}`}>
      <h4 id={`calc-${kind}`} className="text-[14px] font-bold text-ink">
        {accrualTitle(kind, primary)}
      </h4>

      {accrual.periods.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">
          За выбранный период начисление не производится — просрочки нет.
        </p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-[13px]">
            <caption className="sr-only">
              Расчёт по периодам: период, дни, база, ставка, сумма
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={headCls}>
                  Период
                </th>
                <th scope="col" className={headCls}>
                  Дней
                </th>
                <th scope="col" className={headCls}>
                  База
                </th>
                <th scope="col" className={headCls}>
                  Ставка
                </th>
                <th scope="col" className={headCls}>
                  Сумма
                </th>
              </tr>
            </thead>
            <tbody>
              {accrual.periods.map((p) => (
                <tr key={`${p.from}-${p.to}-${p.base}`} className="border-b border-border-soft">
                  <td className={cellCls}>
                    {formatDate(p.from)} — {formatDate(p.to)}
                  </td>
                  <td className={`${cellCls} num`}>{p.days}</td>
                  <td className={`${cellCls} num`}>{formatMoney(p.base)}</td>
                  <td className={`${cellCls} num`}>
                    {kind === "penalty"
                      ? formatRateHundredths(p.rate)
                      : formatPercentAnnual(p.rate)}
                  </td>
                  <td className={`${cellCls} num`}>{formatMoney(p.sum)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className={`${cellCls} font-bold`} colSpan={4}>
                  Итого за {accrual.days} дн.
                </td>
                <td className={`${cellCls} num font-bold`}>{formatMoney(accrual.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {accrual.capped && (
        <p className="mt-2 rounded-[var(--radius-m)] bg-waiting-bg px-2.5 py-1.5 text-[13px] text-waiting-fg">
          Применён договорный потолок неустойки {formatMoney(accrual.capAbs)}
          {accrual.capSource !== null && ` (${accrual.capSource})`}. Начислено по периодам{" "}
          {formatMoney(periodsSum)} — итог ограничен потолком, поэтому сумма строк больше итога.
        </p>
      )}
    </section>
  );
}

export function CalcBreakdown({ calc }: { calc: SanctionCalc }) {
  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-3">
      <h3 className="text-[15px] font-bold text-ink">Расчёт санкций</h3>

      <dl className="mt-2 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">База начисления</dt>
          <dd className="num text-ink">{formatMoney(calc.base)}</dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">Период начисления</dt>
          {/* «Периода нет» решает сервер (`noAccrualPeriod`): последний день может оказаться раньше
              первого — просрочки ещё нет либо обязательство снято/исполнено до начала начисления.
              Диапазон «с 02.06.2026 по 20.05.2026» читался бы как ошибка расчёта. */}
          <dd className="text-ink">
            {calc.noAccrualPeriod
              ? "начисление не велось"
              : calc.from === null || calc.to === null
                ? "—"
                : `${formatDate(calc.from)} — ${formatDate(calc.to)}`}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">Срок исполнения по системе</dt>
          <dd className="text-ink">
            {formatDate(calc.dueComputed)}
            {calc.dueShifted && (
              <span className="stamp ml-1" title="Перенос по ст. 193 ГК РФ">
                ⤷ перенос
              </span>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">Начисление ведётся с</dt>
          <dd className="text-ink">{formatDate(calc.from)}</dd>
        </div>
      </dl>

      {calc.accrualOverride !== null && (
        <p className="mt-2 rounded-[var(--radius-m)] bg-waiting-bg px-2.5 py-1.5 text-[13px] text-waiting-fg">
          Дату начала начисления задал юрист: {formatDate(calc.accrualOverride.date)}. Основание:{" "}
          {calc.accrualOverride.reason}. Срок по данным системы — {formatDate(calc.dueComputed)}.
        </p>
      )}

      {calc.notices
        .filter((n) => n.code !== "accrual_due_overridden" && n.code !== "cap_applied")
        .map((n) => (
          <p
            key={n.code}
            role="status"
            className="mt-2 rounded-[var(--radius-m)] bg-bg px-2.5 py-1.5 text-[13px] text-muted"
          >
            {n.message}
            {/* «Подробнее» — только у тех пояснений, которые описывают ПРАВИЛО, а не факт
                расчёта: «потолок не применён, потому что не заполнена база» человек читает как
                отказ и идёт спрашивать, а ответ есть в статье. */}
            {n.code === "cap_base_missing" && (
              <>
                {" "}
                <HelpLink topic="penaltyCapBaseMissing" />
              </>
            )}
          </p>
        ))}

      {calc.penalty !== null && (
        <AccrualTable kind="penalty" accrual={calc.penalty} primary={calc.primary} />
      )}
      {calc.interest395 !== null && (
        <AccrualTable kind="interest395" accrual={calc.interest395} primary={calc.primary} />
      )}

      <LegalDisclaimer />
    </div>
  );
}

/** Сложение денег строками через целые копейки (правило 2 — без float). */
function addMoney(a: string, b: string): string {
  const k = toKopecks(a) + toKopecks(b);
  const sign = k < 0n ? "-" : "";
  const abs = k < 0n ? -k : k;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}
function toKopecks(v: string): bigint {
  const [whole = "0", frac = ""] = v.split(".");
  return BigInt(whole) * 100n + BigInt(frac.padEnd(2, "0").slice(0, 2));
}
